import { useState, useCallback } from 'react';
import { apiCall, handleError, useLoadingState, useOfflineDetection } from '@/lib/error-handler';
import { toast } from '@/hooks/use-toast';

interface UseApiOptions {
  showSuccessToast?: boolean;
  successMessage?: string;
  loadingKey?: string;
  onSuccess?: (data: any) => void;
  onError?: (error: any) => void;
}

interface UseApiReturn<T> {
  data: T | null;
  loading: boolean;
  error: any;
  execute: (...args: any[]) => Promise<T | null>;
  reset: () => void;
}

export function useApi<T = any>(
  apiFunction: (...args: any[]) => Promise<T>,
  options: UseApiOptions = {}
): UseApiReturn<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<any>(null);
  const [globalLoading, setGlobalLoading] = useLoadingState(options.loadingKey || 'default');
  const [localLoading, setLocalLoading] = useState(false);
  const isOffline = useOfflineDetection();

  const loading = options.loadingKey ? globalLoading : localLoading;
  const setLoading = options.loadingKey ? setGlobalLoading : setLocalLoading;

  const execute = useCallback(async (...args: any[]): Promise<T | null> => {
    if (isOffline) {
      const offlineError = new Error('You are currently offline. Please check your connection.');
      setError(offlineError);
      handleError(offlineError, { operation: 'API call', offline: true });
      return null;
    }

    try {
      setLoading(true);
      setError(null);

      const result = await apiCall(
        () => apiFunction(...args),
        `API call: ${apiFunction.name}`,
        options.showSuccessToast,
        options.successMessage
      );

      setData(result);
      
      if (options.onSuccess) {
        options.onSuccess(result);
      }

      return result;
    } catch (err) {
      setError(err);
      
      if (options.onError) {
        options.onError(err);
      }

      return null;
    } finally {
      setLoading(false);
    }
  }, [apiFunction, isOffline, setLoading, options]);

  const reset = useCallback(() => {
    setData(null);
    setError(null);
    setLoading(false);
  }, [setLoading]);

  return {
    data,
    loading,
    error,
    execute,
    reset
  };
}

// Hook for mutations (create, update, delete operations)
export function useMutation<T = any, P = any>(
  mutationFunction: (params: P) => Promise<T>,
  options: UseApiOptions = {}
): {
  mutate: (params: P) => Promise<T | null>;
  loading: boolean;
  error: any;
  reset: () => void;
} {
  const [error, setError] = useState<any>(null);
  const [globalLoading, setGlobalLoading] = useLoadingState(options.loadingKey || 'mutation');
  const [localLoading, setLocalLoading] = useState(false);
  const isOffline = useOfflineDetection();

  const loading = options.loadingKey ? globalLoading : localLoading;
  const setLoading = options.loadingKey ? setGlobalLoading : setLocalLoading;

  const mutate = useCallback(async (params: P): Promise<T | null> => {
    if (isOffline) {
      const offlineError = new Error('You are currently offline. Please check your connection.');
      setError(offlineError);
      handleError(offlineError, { operation: 'Mutation', offline: true });
      return null;
    }

    try {
      setLoading(true);
      setError(null);

      const result = await apiCall(
        () => mutationFunction(params),
        `Mutation: ${mutationFunction.name}`,
        options.showSuccessToast,
        options.successMessage
      );

      if (options.onSuccess) {
        options.onSuccess(result);
      }

      return result;
    } catch (err) {
      setError(err);
      
      if (options.onError) {
        options.onError(err);
      }

      return null;
    } finally {
      setLoading(false);
    }
  }, [mutationFunction, isOffline, setLoading, options]);

  const reset = useCallback(() => {
    setError(null);
    setLoading(false);
  }, [setLoading]);

  return {
    mutate,
    loading,
    error,
    reset
  };
}

// Hook for queries (read operations)
export function useQuery<T = any>(
  queryFunction: () => Promise<T>,
  options: UseApiOptions & {
    enabled?: boolean;
    refetchOnWindowFocus?: boolean;
    refetchInterval?: number;
  } = {}
): UseApiReturn<T> & {
  refetch: () => Promise<T | null>;
} {
  const {
    enabled = true,
    refetchOnWindowFocus = false,
    refetchInterval,
    ...apiOptions
  } = options;

  const api = useApi(queryFunction, apiOptions);

  // Auto-execute on mount if enabled
  React.useEffect(() => {
    if (enabled) {
      api.execute();
    }
  }, [enabled]);

  // Refetch on window focus
  React.useEffect(() => {
    if (!refetchOnWindowFocus) return;

    const handleFocus = () => {
      if (enabled && !api.loading) {
        api.execute();
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [refetchOnWindowFocus, enabled, api.loading]);

  // Refetch on interval
  React.useEffect(() => {
    if (!refetchInterval || !enabled) return;

    const interval = setInterval(() => {
      if (!api.loading) {
        api.execute();
      }
    }, refetchInterval);

    return () => clearInterval(interval);
  }, [refetchInterval, enabled, api.loading]);

  return {
    ...api,
    refetch: api.execute
  };
}

// Hook for handling form submissions with validation
export function useFormSubmission<T = any, P = any>(
  submitFunction: (data: P) => Promise<T>,
  options: UseApiOptions & {
    onValidationError?: (errors: any) => void;
  } = {}
) {
  const mutation = useMutation(submitFunction, {
    ...options,
    showSuccessToast: options.showSuccessToast ?? true,
    successMessage: options.successMessage || 'Form submitted successfully'
  });

  const handleSubmit = useCallback(async (data: P, onSuccess?: () => void) => {
    try {
      const result = await mutation.mutate(data);
      
      if (result && onSuccess) {
        onSuccess();
      }
      
      return result;
    } catch (error: any) {
      // Handle validation errors specifically
      if (error.type === 'validation' && options.onValidationError) {
        options.onValidationError(error.originalError?.response?.data?.errors || {});
      }
      
      throw error;
    }
  }, [mutation, options.onValidationError]);

  return {
    ...mutation,
    handleSubmit
  };
}

// React import for hooks
import React from 'react'; 